/** Conversational tour status steps for the terminal assistant (v2 — 5 stages). */
import { todayISO, fmtAmt } from './utils.js';
import { isYes, isNo, isSkip } from './assistant-ticket-new.js';
import {
  parseSmartDateOnly,
  validateSmartDate,
  DATE_HINT,
} from './assistant-datetime.js';

const ROOM_TYPES = ['Single', 'Double', 'Triple', 'Family'];

function sd(lead, status) {
  return lead?.extra?.stage_data?.[status] || {};
}

function pick(...vals) {
  return vals.find((v) => v != null && v !== '');
}

function validateYn(v) {
  if (isYes(v) || isNo(v)) return null;
  return 'Reply Y or N.';
}

function parseYn(v) {
  if (isYes(v)) return true;
  if (isNo(v)) return false;
  return null;
}

export function tourPaxFromLead(lead = {}, answers = {}) {
  const extra = lead.extra || {};
  const tqr = extra.tour_quote_request || {};
  if (answers.pax_adt != null) {
    return {
      adt: Math.max(1, Number(answers.pax_adt) || 1),
      chd: Number(answers.pax_chd) || 0,
      inf: Number(answers.pax_inf) || 0,
    };
  }
  const qPax = sd(lead, 'quotation').pax;
  const raw = qPax || tqr.pax || extra.pax || { adt: 1, chd: 0, inf: 0 };
  return {
    adt: Math.max(1, Number(raw.adt) || 1),
    chd: Math.max(0, Number(raw.chd) || 0),
    inf: Math.max(0, Number(raw.inf) || 0),
  };
}

function parseLkr(v) {
  const n = Number(String(v ?? '').trim().replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

function validatePositiveLkr(v) {
  const n = parseLkr(v);
  if (!Number.isFinite(n) || n <= 0) return 'Enter amount in LKR.';
  return null;
}

function validateOptionalLkr(v) {
  if (isSkip(v)) return null;
  const n = parseLkr(v);
  if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR.';
  return null;
}

function parseOptionalLkr(v) {
  if (isSkip(v)) return null;
  const n = parseLkr(v);
  return Number.isFinite(n) ? n : null;
}

function parsePerPersonRates(answers, pax, prefix) {
  const adt = Number(answers[`${prefix}_adt`]);
  const rates = { adt };
  if (pax.chd > 0) {
    const chd = answers[`${prefix}_chd`];
    rates.chd = isSkip(chd) || chd == null || chd === '' ? adt : Number(chd);
  }
  if (pax.inf > 0) {
    const inf = answers[`${prefix}_inf`];
    rates.inf = isSkip(inf) || inf == null || inf === '' ? 0 : Number(inf);
  }
  return rates;
}

export function tourRatesTotal(rates, pax = {}) {
  if (!rates?.adt && rates?.adt !== 0) return 0;
  let total = (rates.adt || 0) * (pax.adt || 0);
  if (pax.chd > 0) total += (rates.chd ?? rates.adt ?? 0) * pax.chd;
  if (pax.inf > 0) total += (rates.inf ?? 0) * pax.inf;
  return Math.round(total);
}

function mergePerPersonCosts(agent, flight, visa, pax) {
  const out = { adt: (agent.adt || 0) + (flight?.adt || 0) + (visa?.adt || 0) };
  if (pax.chd > 0) {
    out.chd = (agent.chd ?? agent.adt ?? 0) + (flight?.chd || 0) + (visa?.chd || 0);
  }
  if (pax.inf > 0) {
    out.inf = (agent.inf ?? 0) + (flight?.inf || 0) + (visa?.inf || 0);
  }
  return out;
}

function quotationCostBreakdown(answers, lead) {
  const pax = tourPaxFromLead(lead, answers);
  const agent = parsePerPersonRates(answers, pax, 'agent_rate');
  const flight = optionalPerPersonRates(answers, pax, 'flight_cost');
  const visa = optionalPerPersonRates(answers, pax, 'visa_cost');
  const totalPer = mergePerPersonCosts(agent, flight, visa, pax);
  const grandTotal = tourRatesTotal(totalPer, pax);
  return { pax, agent, flight, visa, totalPer, grandTotal };
}

function formatCostSummary(answers, lead) {
  const { pax, totalPer, grandTotal } = quotationCostBreakdown(answers, lead);
  const lines = [`Total cost per adult: ${fmtAmt(totalPer.adt)} (tour + flight + visa)`];
  if (pax.chd > 0) lines.push(`Total cost per child: ${fmtAmt(totalPer.chd ?? totalPer.adt)}`);
  if (pax.inf > 0) lines.push(`Total cost per infant: ${fmtAmt(totalPer.inf ?? 0)}`);
  lines.push(`Grand total cost: ${fmtAmt(grandTotal)}`);
  lines.push('Press Enter to continue to client quote.');
  return lines.join('\n');
}

function formatSellSummary(answers, lead) {
  const { pax, grandTotal: costTotal } = quotationCostBreakdown(answers, lead);
  const finalQuote = parsePerPersonRates(answers, pax, 'final_rate');
  const sellTotal = tourRatesTotal(finalQuote, pax);
  const profit = sellTotal - costTotal;
  const lines = [`Client quote total: ${fmtAmt(sellTotal)}`, `Total cost: ${fmtAmt(costTotal)}`, `Profit: ${fmtAmt(profit)}`];
  lines.push('Press Enter to continue.');
  return lines.join('\n');
}

export function tourQuotedTotal(lead = {}) {
  const extra = lead.extra || {};
  const qSd = extra.stage_data?.quotation || extra.stage_data?.final_quote_sent || {};
  const advanceSd = extra.stage_data?.tour_accepted_advance || {};
  const pax = qSd.pax || extra.pax || extra.tour_quote_request?.pax || { adt: 1, chd: 0, inf: 0 };
  const fromRates = tourRatesTotal(qSd.final_quote, pax) || tourRatesTotal(extra.final_quote, pax);
  const raw = lead.amount ?? qSd.sell_total ?? qSd.total ?? fromRates ?? advanceSd.quoted_total ?? extra.quoted_total;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

function tourBalanceDue(lead, advanceAmount) {
  const quote = tourQuotedTotal(lead);
  const advance = Number(advanceAmount);
  if (!Number.isFinite(advance)) return quote;
  return Math.max(0, Math.round(quote - advance));
}

function perPersonOptionalSteps(prefix, label, pax, existing = {}, { requiredAdt = false } = {}) {
  const steps = [{
    key: `${prefix}_adt`,
    ask: () => `${label} per adult (LKR)?`,
    optional: !requiredAdt,
    validate: requiredAdt ? validatePositiveLkr : validateOptionalLkr,
    parse: (v) => {
      if (isSkip(v) && !requiredAdt) return null;
      const n = parseLkr(v);
      return Number.isFinite(n) ? Math.round(n) : NaN;
    },
    default: () => pick(existing.adt),
  }];
  if (pax.chd > 0) {
    steps.push({
      key: `${prefix}_chd`,
      ask: () => `${label} per child (LKR)? — ${pax.chd} CHD`,
      hint: 'Skip = same as adult',
      optional: true,
      validate: validateOptionalLkr,
      parse: parseOptionalLkr,
      default: () => pick(existing.chd),
    });
  }
  if (pax.inf > 0) {
    steps.push({
      key: `${prefix}_inf`,
      ask: () => `${label} per infant (LKR)? — ${pax.inf} INF`,
      hint: 'Skip = free',
      optional: true,
      validate: validateOptionalLkr,
      parse: parseOptionalLkr,
      default: () => pick(existing.inf),
    });
  }
  return steps;
}

function quotationSteps(lead, stage = {}) {
  const pax = tourPaxFromLead(lead);
  const existing = stage.agent_quote ? stage : sd(lead, 'quotation');
  const steps = [
    ...perPersonOptionalSteps('agent_rate', 'Agent net cost', pax, existing.agent_quote || {}, { requiredAdt: true }),
    ...perPersonOptionalSteps('flight_cost', 'Flight fare', pax, existing.flight_cost || {}),
    ...perPersonOptionalSteps('visa_cost', 'Visa fee', pax, existing.visa_cost || {}),
    {
      key: '_cost_summary',
      ask: (answers, lead) => formatCostSummary(answers, lead),
      optional: true,
      validate: () => null,
      parse: () => true,
    },
    ...perPersonOptionalSteps('final_rate', 'Final quote', pax, existing.final_quote || {}, { requiredAdt: true }),
    {
      key: '_sell_summary',
      ask: (answers, lead) => formatSellSummary(answers, lead),
      optional: true,
      validate: () => null,
      parse: () => true,
    },
    {
      key: 'add_hotel_rooms',
      ask: 'Add hotel room details? (Y/N)',
      optional: true,
      validate: validateYn,
      parse: parseYn,
      default: () => (existing.hotel_rooms?.length ? true : null),
    },
    {
      key: 'hotel_rooms_summary',
      ask: 'Room details? e.g. Double x1 (2 pax), Single x1 (1 pax)',
      hint: 'Type skip if not adding rooms',
      optional: true,
      when: (answers) => answers.add_hotel_rooms === true,
      parse: (v) => (isSkip(v) ? null : String(v).trim()),
      default: () => pick(existing.hotel_rooms_summary, existing.hotel_notes),
    },
    {
      key: 'supplier_note',
      ask: 'Supplier / DMC notes?',
      hint: 'Optional — skip if none',
      optional: true,
      parse: (v) => (isSkip(v) ? null : String(v).trim()),
      default: () => pick(existing.supplier_note, stage.note),
    },
  ];
  return steps;
}

export function tourStageSteps(status, lead = {}, answers = {}) {
  const extra = lead.extra || {};
  const stage = sd(lead, status);
  const tqr = extra.tour_quote_request || {};

  switch (status) {
    case 'new':
      return [];
    case 'quotation':
      return quotationSteps(lead, stage);
    case 'tour_accepted_advance':
      return [
        {
          key: 'travel_date',
          ask: 'Travel date?',
          hint: DATE_HINT,
          validate: validateSmartDate,
          parse: (v) => parseSmartDateOnly(v),
          default: () => pick(extra.travel_date, stage.travel_date, tqr.date_from, lead.depart_date),
        },
        {
          key: 'advance_amount',
          ask: () => {
            const q = tourQuotedTotal(lead);
            return q ? `Advance received (LKR)? Quote: ${fmtAmt(q)}` : 'Advance received (LKR)?';
          },
          validate: (v) => {
            const n = Number(String(v).trim());
            if (!Number.isFinite(n) || n < 0) return 'Enter advance amount.';
            return null;
          },
          parse: (v) => Number(String(v).trim()),
          default: () => pick(extra.advance_paid, stage.advance_amount),
        },
        {
          key: 'advance_date',
          ask: 'Advance paid on?',
          hint: DATE_HINT,
          validate: validateSmartDate,
          parse: (v) => parseSmartDateOnly(v) || todayISO(),
          default: () => pick(extra.advance_date, stage.advance_date, todayISO()),
        },
        {
          key: 'balance_collect_date',
          ask: 'Balance collect by?',
          hint: DATE_HINT,
          when: (a, ld) => {
            const advance = a.advance_amount ?? sd(ld, 'tour_accepted_advance').advance_amount ?? ld?.extra?.advance_paid;
            if (advance == null || advance === '') return false;
            return tourBalanceDue(ld, advance) > 0;
          },
          validate: validateSmartDate,
          parse: (v) => parseSmartDateOnly(v),
          default: () => pick(extra.balance_collect_date, stage.balance_collect_date),
        },
      ];
    case 'tour_accepted_progress':
      return [
        {
          key: 'progress_notes',
          ask: 'Progress notes?',
          hint: 'e.g. EK 654 booked, visa applied 260910 — skip if none',
          optional: true,
          parse: (v) => (isSkip(v) ? null : String(v).trim()),
          default: () => pick(extra.ticket_booking_details, stage.progress_notes, stage.ticket_details),
        },
        {
          key: 'followup_date',
          ask: 'Next follow-up date?',
          hint: DATE_HINT,
          optional: true,
          validate: (v) => (isSkip(v) ? null : validateSmartDate(v)),
          parse: (v) => (isSkip(v) ? null : parseSmartDateOnly(v)),
          default: () => pick(lead.followup_date, stage.followup_date),
        },
        {
          key: 'ticket_booked_on',
          ask: 'Tickets booked on?',
          hint: DATE_HINT,
          optional: true,
          validate: (v) => (isSkip(v) ? null : validateSmartDate(v)),
          parse: (v) => (isSkip(v) ? null : parseSmartDateOnly(v)),
          default: () => pick(extra.ticket_booked_on, stage.ticket_booked_on),
        },
        {
          key: 'visa_issued_date',
          ask: 'Visa issued date?',
          hint: DATE_HINT,
          optional: true,
          validate: (v) => (isSkip(v) ? null : validateSmartDate(v)),
          parse: (v) => (isSkip(v) ? null : parseSmartDateOnly(v)),
          default: () => pick(extra.visa_issued_date, stage.visa_issued_date, stage.visa_applied_date),
        },
      ];
    case 'tour_finalised':
      return [{
        key: 'note',
        ask: 'Closing notes?',
        optional: true,
        parse: (v) => (isSkip(v) ? null : String(v).trim()),
      }];
    default:
      return [];
  }
}

function optionalPerPersonRates(d, pax, prefix) {
  const raw = d[`${prefix}_adt`];
  if (raw == null || isSkip(raw)) return null;
  if (typeof raw === 'number' && !Number.isFinite(raw)) return null;
  return parsePerPersonRates(d, pax, prefix);
}

export function buildQuotationStageData(lead, d) {
  const pax = tourPaxFromLead(lead, d);
  const agentQuote = parsePerPersonRates(d, pax, 'agent_rate');
  const flightCost = optionalPerPersonRates(d, pax, 'flight_cost');
  const visaCost = optionalPerPersonRates(d, pax, 'visa_cost');
  const totalCostPer = mergePerPersonCosts(agentQuote, flightCost, visaCost, pax);
  const costTotal = tourRatesTotal(totalCostPer, pax);
  const finalQuote = parsePerPersonRates(d, pax, 'final_rate');
  const sellTotal = tourRatesTotal(finalQuote, pax);
  const profitTotal = sellTotal - costTotal;

  return {
    pax,
    agent_quote: agentQuote,
    flight_cost: flightCost,
    visa_cost: visaCost,
    total_cost_per: totalCostPer,
    cost_total: costTotal,
    final_quote: finalQuote,
    sell_total: sellTotal,
    total: sellTotal,
    profit_total: profitTotal,
    profit_markup: profitTotal,
    hotel_rooms_summary: d.hotel_rooms_summary || null,
    hotel_notes: d.hotel_rooms_summary || null,
    supplier_note: d.supplier_note || null,
    note: d.supplier_note || null,
  };
}

export function buildTourStatusPatchBody(status, lead, answers = {}) {
  const d = { ...answers };
  const body = { status, stage_data: {} };
  const extra = lead.extra || {};

  switch (status) {
    case 'quotation': {
      const qd = buildQuotationStageData(lead, d);
      body.cost_amount = qd.cost_total;
      body.amount = qd.sell_total;
      body.stage_data = qd;
      if (d.supplier_note) body.note = d.supplier_note;
      return body;
    }
    case 'tour_accepted_advance': {
      const quote = tourQuotedTotal(lead);
      const advance = d.advance_amount ?? 0;
      const balance = tourBalanceDue(lead, advance);
      body.amount = quote || null;
      body.stage_data = {
        travel_date: d.travel_date,
        advance_amount: advance,
        advance_date: d.advance_date,
        balance_collect_date: balance > 0 ? d.balance_collect_date : null,
        balance_due: balance,
        quoted_total: quote || null,
      };
      if (advance > 0) body.advance_paid = advance;
      body.followup_date = balance > 0 ? d.balance_collect_date : null;
      if (d.travel_date) body.depart_date = d.travel_date;
      return body;
    }
    case 'tour_accepted_progress':
      body.stage_data = {
        progress_notes: d.progress_notes || null,
        ticket_details: d.progress_notes || null,
        followup_date: d.followup_date || null,
        ticket_booked_on: d.ticket_booked_on || null,
        visa_issued_date: d.visa_issued_date || null,
        visa_applied_date: d.visa_issued_date || null,
      };
      if (d.progress_notes) extra.ticket_booking_details = d.progress_notes;
      if (d.followup_date) body.followup_date = d.followup_date;
      if (d.ticket_booked_on && d.visa_issued_date) {
        body.status = 'tour_finalised';
        body.auto_finalise = true;
      }
      return body;
    case 'tour_finalised':
      body.stage_data = {
        note: d.note || null,
        ticket_booked_on: extra.ticket_booked_on || sd(lead, 'tour_accepted_progress').ticket_booked_on,
        visa_issued_date: extra.visa_issued_date || sd(lead, 'tour_accepted_progress').visa_issued_date,
      };
      if (d.note) body.note = d.note;
      return body;
    default:
      return body;
  }
}

export function validateTourStageAnswers(status, lead, answers = {}) {
  const body = buildTourStatusPatchBody(status, lead, answers);
  const d = body.stage_data || {};

  if (status === 'quotation') {
    const adtCost = Number(d.agent_quote?.adt ?? answers.agent_rate_adt);
    if (!Number.isFinite(adtCost) || adtCost <= 0) return 'Enter agent net cost per adult (LKR).';
    const adtSell = Number(d.final_quote?.adt ?? answers.final_rate_adt);
    if (!Number.isFinite(adtSell) || adtSell <= 0) return 'Enter final quote per adult (LKR).';
    if (!body.cost_amount) return 'Enter agent net cost per adult (LKR).';
    if (!body.amount) return 'Enter final quote per adult (LKR).';
  }
  if (status === 'tour_accepted_advance') {
    if (!d.travel_date) return 'Enter travel date.';
    if (d.advance_amount == null) return 'Enter advance amount.';
    if (!d.advance_date) return 'Enter advance date.';
    if (!d.quoted_total) return 'Quotation missing — complete Quotation stage first.';
    if (tourBalanceDue(lead, d.advance_amount) > 0 && !d.balance_collect_date) {
      return 'Enter balance collect date.';
    }
  }
  if (status === 'tour_finalised' && !d.ticket_booked_on && !lead.extra?.ticket_booked_on) {
    return null;
  }
  return null;
}

export { DATE_HINT, ROOM_TYPES };
